import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  MenuItem,
  Divider
} from "@mui/material";
import {
  Add,
  Edit,
  DeleteOutline
} from "@mui/icons-material";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import Swal from "sweetalert2";

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

function Rateplans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    planCode: "",
    planName: "",
    description: "",
    status: "active"
  });

  /* ================= FETCH ================= */
  const fetchPlans = async () => {
    const snap = await getDocs(collection(db, "rate_plans"));
    setPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  /* ================= SAVE ================= */
  const savePlan = async () => {
    if (!form.planCode || !form.planName) {
      Swal.fire("Required", "Plan Code & Name are required", "warning");
      return;
    }

    try {
      if (editId) {
        await updateDoc(doc(db, "rate_plans", editId), form);
        Swal.fire("Updated", "Rate plan updated", "success");
      } else {
        await addDoc(collection(db, "rate_plans"), {
          ...form,
          createdAt: serverTimestamp()
        });
        Swal.fire("Added", "Rate plan added", "success");
      }

      setOpen(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  /* ================= DELETE ================= */
  const deletePlan = async (id) => {
    const ok = await Swal.fire({
      title: "Delete rate plan?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      background: colors.calPolyGreen,
      color: colors.vanilla,
      confirmButtonColor: colors.goldenrod,
      cancelButtonColor: colors.darkGreen
    });

    if (ok.isConfirmed) {
      await deleteDoc(doc(db, "rate_plans", id));
      Swal.fire("Deleted", "Rate plan removed", "success");
      fetchPlans();
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setEditId(null);
    setForm({
      planCode: "",
      planName: "",
      description: "",
      status: "active"
    });
  };

  const openEdit = (plan) => {
    setEditId(plan.id);
    setForm({
      planCode: plan.planCode,
      planName: plan.planName,
      description: plan.description,
      status: plan.status
    });
    setOpen(true);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography fontSize={26} fontWeight={800} sx={{ color: colors.vanilla }}>
            Rate Plans
          </Typography>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            sx={{
              borderRadius: "10px",
              px: 3,
              py: 1,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.goldenrod,
              color: colors.darkGreen,
              boxShadow: "0 4px 12px rgba(209, 165, 5, 0.2)",
              "&:hover": { bgcolor: colors.gold }
            }}
          >
            Add Rate Plan
          </Button>
        </Stack>

        {/* ================= TABLE ================= */}
        <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: colors.darkGreen }}>
                  <TableRow>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Plan Code</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Plan Name</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Description</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                      <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{p.planCode}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{p.planName}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{p.description}</TableCell>
                      <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        <Chip
                          label={p.status.toUpperCase()}
                          color={p.status === "active" ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        <IconButton sx={{ color: colors.gold }} onClick={() => openEdit(p)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deletePlan(p.id)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!plans.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                        No rate plans found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* ================= DIALOG ================= */}
        <Dialog 
          open={open} 
          onClose={() => setOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow: "0 24px 50px rgba(0,0,0,0.5)",
              border: `1px solid ${colors.goldenrod}`,
              bgcolor: colors.calPolyGreen,
              color: colors.vanilla,
              overflow: "hidden"
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: colors.darkGreen, color: colors.vanilla, fontWeight: 700, py: 2 }}>
            {editId ? "Edit Rate Plan" : "Add Rate Plan"}
          </DialogTitle>
          <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

          <DialogContent dividers sx={{ borderColor: colors.goldenrod, opacity: 0.9, p: 3, bgcolor: colors.calPolyGreen }}>
            <Stack spacing={2.5}>
              <TextField
                label="Plan Code"
                value={form.planCode}
                onChange={(e) =>
                  setForm({ ...form, planCode: e.target.value })
                }
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{ style: { color: colors.vanilla } }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    backgroundColor: colors.darkGreen,
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  } 
                }}
              />

              <TextField
                label="Plan Name"
                value={form.planName}
                onChange={(e) =>
                  setForm({ ...form, planName: e.target.value })
                }
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{ style: { color: colors.vanilla } }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    backgroundColor: colors.darkGreen,
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  } 
                }}
              />

              <TextField
                label="Description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                multiline
                rows={3}
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{ style: { color: colors.vanilla } }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    backgroundColor: colors.darkGreen,
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  } 
                }}
              />

              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value })
                }
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: colors.vanilla } }}
                sx={{ 
                  color: colors.vanilla,
                  backgroundColor: colors.darkGreen,
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  },
                  '& .MuiSelect-select': { color: colors.vanilla },
                  '& .MuiSvgIcon-root': { color: colors.vanilla }
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

          <DialogActions sx={{ p: 2.5, bgcolor: colors.darkGreen, gap: 1 }}>
            <Button onClick={() => setOpen(false)} sx={{ color: colors.vanilla, textTransform: "none", fontWeight: 700 }}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={savePlan}
              sx={{
                bgcolor: colors.goldenrod, 
                color: colors.darkGreen, 
                fontWeight: 700, 
                textTransform: "none", 
                borderRadius: "8px",
                '&:hover': { bgcolor: colors.gold }
              }}
            >
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default Rateplans;